<template>
    <aside
        :class="[
            'flex flex-col bg-gray-900 text-white h-screen fixed top-0 left-0 z-[900] transition-all duration-200 pt-14',
            isCollapsed ? 'w-[72px]' : 'w-64'
        ]"
    >
        <!-- Toggle Button -->
        <div class="flex justify-end p-3">
            <button
                @click="toggleSidebar"
                class="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
            >
                <BaseIcon
                    :name="isCollapsed ? 'chevron-right' : 'chevron-left'"
                    size="16"
                    color="currentColor"
                />
            </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 overflow-y-auto pt-1 sidebar-scrollbar">
            <!-- Loading permissions -->
            <div v-if="!authStore.isAdmin && permissionsStore.loading && !permissionsStore.loaded" class="flex flex-col items-center justify-center py-8">
                <div class="w-5 h-5 border-2 border-gray-700 border-t-gray-400 rounded-full animate-spin"></div>
            </div>

            <ul v-else class="list-none p-0 m-0">
                <template v-for="(item, index) in filteredMenuItems" :key="index">
                    <!-- Section Header -->
                    <li v-if="item.type === 'section'" class="mt-6 mb-2 mx-4">
                        <span
                            v-if="!isCollapsed"
                            class="text-[11px] font-semibold tracking-wider text-gray-500 uppercase block px-2"
                        >
                            {{ item.title }}
                        </span>
                        <div v-else class="h-px bg-gray-700 mx-2"></div>
                    </li>

                    <!-- Menu with Submenu -->
                    <li
                        v-else-if="item.children"
                        :class="[
                            'mx-2 my-0.5 rounded-lg overflow-hidden transition-colors',
                            {
                                'bg-gray-800/50': expandedMenus.includes(index)
                            }
                        ]"
                    >
                        <div
                            @click="toggleSubmenu(index)"
                            :class="[
                                'flex items-center px-3 py-2.5 cursor-pointer transition-all rounded-lg text-sm',
                                isParentActive(item.children)
                                    ? 'text-white bg-gray-800'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                            ]"
                        >
                            <div class="flex items-center justify-center w-5 h-5 mr-3">
                                <BaseIcon :name="item.iconName" size="18" />
                            </div>
                            <span v-if="!isCollapsed" class="flex-1 font-medium">{{ item.title }}</span>
                            <BaseIcon
                                v-if="!isCollapsed"
                                name="chevron-down"
                                size="14"
                                :class="[
                                    'ml-auto transition-transform duration-200 text-gray-500',
                                    expandedMenus.includes(index) ? 'rotate-180' : ''
                                ]"
                            />
                        </div>

                        <!-- Submenu -->
                        <transition
                            name="submenu"
                            @enter="onEnter"
                            @leave="onLeave"
                        >
                            <ul
                                v-if="!isCollapsed && expandedMenus.includes(index)"
                                class="list-none p-0 m-0 overflow-hidden"
                            >
                                <li
                                    v-for="(child, childIndex) in item.children"
                                    :key="childIndex"
                                    class="m-0"
                                >
                                    <router-link
                                        :to="child.route"
                                        :class="[
                                            'flex items-center py-2 px-3 pl-11 no-underline transition-all text-sm rounded-lg mx-1',
                                            isActive(child.route)
                                                ? 'text-primary bg-primary/10 font-medium'
                                                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                                        ]"
                                    >
                                        <span
                                            :class="[
                                                'w-1.5 h-1.5 rounded-full mr-3 transition-all',
                                                isActive(child.route) ? 'bg-primary' : 'bg-gray-600'
                                            ]"
                                        ></span>
                                        <span>{{ child.title }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </transition>
                    </li>

                    <!-- Regular Menu Item -->
                    <li
                        class="mx-2 my-0.5 rounded-lg overflow-hidden transition-colors"
                        v-else
                    >
                        <router-link
                            :to="item.route"
                            :class="[
                                'flex items-center px-3 py-2.5 no-underline transition-all rounded-lg text-sm',
                                isActive(item.route)
                                    ? 'text-white bg-primary font-medium'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                            ]"
                        >
                            <div class="flex items-center justify-center w-5 h-5 mr-3">
                                <BaseIcon :name="item.iconName" size="18" />
                            </div>
                            <span v-if="!isCollapsed" class="font-medium">{{ item.title }}</span>
                        </router-link>
                    </li>
                </template>
            </ul>
        </nav>

        <!-- Footer Support -->
        <div class="p-4 border-t border-gray-800">
            <div
                @click="showSupport"
                class="flex items-center cursor-pointer p-2.5 rounded-lg transition-colors text-gray-500 hover:bg-gray-800 hover:text-gray-300"
            >
                <div class="flex items-center justify-center w-5 h-5 mr-3">
                    <BaseIcon name="information-circle" size="18" />
                </div>
                <span v-if="!isCollapsed" class="text-sm">Support</span>
            </div>
        </div>
    </aside>
</template>

<script>
import { usePermissionsStore } from '@/store/modules/permissions';
import { useAuthStore } from '@/store/modules/auth';

export default {
    name: 'SideBar',
    data() {
        return {
            isCollapsed: false,
            expandedMenus: [],
            menuItems: [
                // ACCES RAPIDE
                {
                    title: 'Tableau de bord',
                    iconName: 'home',
                    route: '/dashboard',
                    permission: 'dashboard'
                },
                {
                    title: 'Caisses',
                    iconName: 'shopping-cart',
                    route: '/cash-registers',
                    permission: 'cash-registers'
                },

                // SECTION VENTES
                {
                    type: 'section',
                    title: 'VENTES & TRANSACTIONS'
                },
                {
                    title: 'Ventes',
                    iconName: 'cash',
                    permissions: ['sales', 'cash-registers'],
                    children: [
                        {
                            title: 'Historique des ventes',
                            route: '/sales',
                            permission: 'sales'
                        },
                        {
                            title: 'Historique des caisses',
                            route: '/cash-registers-history',
                            permission: 'cash-registers'
                        }
                    ]
                },
                {
                    title: 'Factures Proforma',
                    iconName: 'receipt',
                    route: '/proforma-invoices',
                    permission: 'sales'
                },
                {
                    title: 'Clients',
                    iconName: 'users',
                    route: '/customers',
                    permission: 'customers'
                },

                // SECTION STOCK
                {
                    type: 'section',
                    title: 'STOCK & CATALOGUE'
                },
                {
                    title: 'Gestion de stock',
                    iconName: 'tag',
                    permissions: ['products', 'categories', 'stock-withdrawals'],
                    children: [
                        {
                            title: 'Produits',
                            route: '/products',
                            permission: 'products'
                        },
                        {
                            title: 'Categories',
                            route: '/categories',
                            permission: 'categories'
                        },
                        {
                            title: 'Retraits de stock',
                            route: '/stock-withdrawals',
                            permission: 'stock-withdrawals'
                        }
                    ]
                },
                {
                    title: 'Inventaire',
                    iconName: 'document',
                    permissions: ['physical-inventories', 'product-regularizations'],
                    children: [
                        {
                            title: 'Nouvel inventaire',
                            route: '/inventory/new',
                            permission: 'physical-inventories.create'
                        },
                        {
                            title: 'Historique inventaires',
                            route: '/inventory/physical',
                            permission: 'physical-inventories'
                        },
                        {
                            title: 'Regularisations',
                            route: '/product-regularizations',
                            permission: 'product-regularizations'
                        }
                    ]
                },

                // SECTION ACHATS
                {
                    type: 'section',
                    title: 'ACHATS & APPROVISIONNEMENT'
                },
                {
                    title: 'Fournisseurs',
                    iconName: 'truck',
                    route: '/suppliers',
                    permission: 'suppliers'
                },
                {
                    title: 'Commandes',
                    iconName: 'receipt',
                    route: '/purchase-orders',
                    permission: 'purchase-orders'
                },
                {
                    title: 'Factures',
                    iconName: 'fa-sharp-duotone fa-light fa-cart-circle-plus',
                    route: '/supplier-invoices',
                    permission: 'supplier-invoices'
                },

                // SECTION ADMINISTRATION
                {
                    type: 'section',
                    title: 'ADMINISTRATION'
                },
                {
                    title: 'Utilisateurs',
                    iconName: 'users',
                    route: '/users',
                    requiresAdmin: true
                },
                {
                    title: 'Notifications',
                    iconName: 'bell',
                    route: '/notifications',
                    permission: 'notifications'
                },
                {
                    title: 'Parametres',
                    iconName: 'cog',
                    route: '/settings',
                    permission: 'company-settings'
                }
            ]
        }
    },
    computed: {
        permissionsStore() {
            return usePermissionsStore();
        },
        authStore() {
            return useAuthStore();
        },
        filteredMenuItems() {
            const result = [];

            for (const item of this.menuItems) {
                // Section headers: ajouter temporairement
                if (item.type === 'section') {
                    result.push(item);
                    continue;
                }

                // Items admin-only
                if (item.requiresAdmin && !this.authStore.isAdmin) {
                    continue;
                }

                // Vérifier la permission de l'item
                if (!this.checkItemPermission(item)) {
                    continue;
                }

                // Items avec sous-menus : filtrer les enfants
                if (item.children) {
                    const filteredChildren = item.children.filter(child =>
                        this.checkItemPermission(child)
                    );
                    if (filteredChildren.length === 0) continue;
                    result.push({ ...item, children: filteredChildren });
                } else {
                    result.push(item);
                }
            }

            // Supprimer les headers de section orphelins
            return this.cleanSectionHeaders(result);
        }
    },
    methods: {
        checkItemPermission(item) {
            if (!item.permission && !item.permissions) return true;
            if (this.authStore.isAdmin) return true;

            if (item.permission) {
                return this.permissionsStore.hasPermission(item.permission);
            }
            if (item.permissions) {
                return this.permissionsStore.hasAnyPermission(item.permissions);
            }
            return true;
        },
        cleanSectionHeaders(items) {
            const cleaned = [];
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type === 'section') {
                    // Check if there's at least one non-section item before the next section
                    let hasContent = false;
                    for (let j = i + 1; j < items.length; j++) {
                        if (items[j].type === 'section') break;
                        hasContent = true;
                        break;
                    }
                    if (hasContent) {
                        cleaned.push(item);
                    }
                } else {
                    cleaned.push(item);
                }
            }
            return cleaned;
        },
        toggleSidebar() {
            this.isCollapsed = !this.isCollapsed;
            if (this.isCollapsed) {
                this.expandedMenus = [];
            }
            this.$emit('sidebar-toggle', this.isCollapsed);
        },
        toggleSubmenu(index) {
            if (this.isCollapsed) {
                return;
            }
            const menuIndex = this.expandedMenus.indexOf(index);
            if (menuIndex > -1) {
                this.expandedMenus.splice(menuIndex, 1);
            } else {
                this.expandedMenus = [index];
            }
        },
        isActive(route) {
            if (!route) return false;
            return this.$route.path === route || this.$route.path.startsWith(`${route}/`);
        },
        isParentActive(children) {
            if (!children) return false;
            return children.some(child => this.isActive(child.route));
        },
        showSupport() {
            alert('Support technique: support@sellhub.com');
        },
        onEnter(el) {
            el.style.maxHeight = '0';
            el.style.opacity = '0';
            requestAnimationFrame(() => {
                el.style.transition = 'all 0.2s ease';
                el.style.maxHeight = el.scrollHeight + 'px';
                el.style.opacity = '1';
            });
        },
        onLeave(el) {
            el.style.maxHeight = el.scrollHeight + 'px';
            el.style.opacity = '1';
            requestAnimationFrame(() => {
                el.style.transition = 'all 0.2s ease';
                el.style.maxHeight = '0';
                el.style.opacity = '0';
            });
        }
    },
    mounted() {
        this.filteredMenuItems.forEach((item, index) => {
            if (item.children && this.isParentActive(item.children)) {
                this.expandedMenus = [index];
            }
        });
    },
    watch: {
        '$route'() {
            if (!this.isCollapsed) {
                let activeMenuIndex = null;
                this.filteredMenuItems.forEach((item, index) => {
                    if (item.children && this.isParentActive(item.children)) {
                        activeMenuIndex = index;
                    }
                });
                if (activeMenuIndex !== null) {
                    this.expandedMenus = [activeMenuIndex];
                }
            }
        }
    }
}
</script>

<style scoped>
/* Custom scrollbar */
.sidebar-scrollbar::-webkit-scrollbar {
    width: 4px;
}

.sidebar-scrollbar::-webkit-scrollbar-track {
    background-color: transparent;
}

.sidebar-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
}

.sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.2);
}

/* Submenu transition */
.submenu-enter-active,
.submenu-leave-active {
    transition: all 0.2s ease;
}

.submenu-enter-from,
.submenu-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}
</style>
